import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  ACCOMMODATION,
  ACCOMMODATION_CHANGE_STATUS,
  ACCOMMODATION_CREATE,
  ACCOMMODATION_DELETE,
  ACCOMMODATION_LIST,
  ACCOMMODATION_STUDENT_INFO,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const accommodation = createAsyncThunk(
  ACCOMMODATION,
  async (params: PayloadCreatorParams<`${typeof API.ACCOMMODATION}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.accommodation(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const accommodationCreate = createAsyncThunk(
  ACCOMMODATION_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.ACCOMMODATION}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.accommodationCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const accommodationDelete = createAsyncThunk(
  ACCOMMODATION_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.ACCOMMODATION}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.accommodationDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const accommodationList = createAsyncThunk(
  ACCOMMODATION_LIST,
  async (params: PayloadCreatorParams<`${typeof API.ACCOMMODATION}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.accommodationList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const accommodationStudentInfo = createAsyncThunk(
  ACCOMMODATION_STUDENT_INFO,
  async (
    params: PayloadCreatorParams<`${typeof API.ACCOMMODATION_STUDENT_INFO}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.accommodationStudentInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const accommodationChangeStatus = createAsyncThunk(
  ACCOMMODATION_CHANGE_STATUS,
  async (params: PayloadCreatorParams<`${typeof API.ACCOMMODATION_CHANGE_STATUS}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.accommodationChangeStatus(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const accommodationClear = createAction(`${ACCOMMODATION}/clear`)

export const accommodationListClear = createAction(`${ACCOMMODATION_LIST}/clear`)

export const accommodationStudentInfoClear = createAction(`${ACCOMMODATION_STUDENT_INFO}/clear`)
